import axios from 'axios';
import { API_URL } from '../config';

/**
 * Get user's AI preferences
 * @returns {Promise} Promise with user AI preferences
 */
export const getUserAIPreferences = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, cannot fetch AI preferences');
      return {
        success: false,
        message: 'Authentication required',
        preferences: {
          ai_provider: 'openai',
          ai_model: 'gpt-4o',
          has_openai_key: false,
          has_ollama_url: false
        }
      };
    }
    
    const response = await axios.get(`${API_URL}/preferences/ai`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching AI preferences:', error);
    // Return a structured error response with default preferences
    return {
      success: false,
      message: error.message || 'Failed to fetch AI preferences',
      preferences: {
        ai_provider: 'openai',
        ai_model: 'gpt-4o',
        has_openai_key: false,
        has_ollama_url: false
      }
    };
  }
};

/**
 * Update user's AI preferences
 * @param {Object} preferences - AI preferences {ai_provider, ai_model}
 * @returns {Promise} Promise with updated preferences
 */
export const updateUserAIPreferences = async (preferences) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, cannot update AI preferences');
      return {
        success: false,
        message: 'Authentication required'
      };
    }
    
    const response = await axios.put(`${API_URL}/preferences/ai`, preferences, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating AI preferences:', error);
    return {
      success: false,
      message: error.message || 'Failed to update AI preferences'
    };
  }
};

/**
 * Get available AI models
 * @returns {Promise} Promise with available AI models
 */
export const getAvailableAIModels = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, returning default AI models');
      return {
        providers: [
          {
            name: 'openai',
            display_name: 'OpenAI',
            models: [
              { id: 'gpt-4o', name: 'GPT-4o' },
              // { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' }
            ]
          },
          {
            name: 'ollama',
            display_name: 'Ollama',
            models: [
              { id: 'llama3', name: 'Llama 3' }
            ]
          }
        ]
      };
    }
    
    const response = await axios.get(`${API_URL}/preferences/ai/models`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available AI models:', error);
    // Return default models if API fails
    return {
      providers: [
        {
          name: 'openai',
          display_name: 'OpenAI',
          models: [
            { id: 'gpt-4o', name: 'GPT-4o' },
            // { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' }
          ]
        },
        {
          name: 'ollama',
          display_name: 'Ollama',
          models: [
            { id: 'llama3', name: 'Llama 3' }
          ]
        }
      ]
    };
  }
};

/**
 * Get user's assigned AI models
 * @returns {Promise} Promise with user's assigned AI models
 */
export const getUserAssignedModels = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, cannot fetch assigned AI models');
      return {
        success: false,
        message: 'Authentication required',
        models: []
      };
    }
    
    const response = await axios.get(`${API_URL}/preferences/ai/user/models`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching assigned AI models:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch assigned AI models',
      models: []
    };
  }
};

/**
 * Set a model as the default for the current user
 * @param {string} provider - AI provider (e.g., 'openai', 'ollama')
 * @param {string} model - AI model (e.g., 'gpt-4o', 'llama3')
 * @returns {Promise} Promise with result
 */
export const setDefaultAIModel = async (provider, model) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, cannot set default AI model');
      return {
        success: false,
        message: 'Authentication required'
      };
    }
    
    // First try to update the user's preferences directly
    try {
      const updateResponse = await updateUserAIPreferences({
        ai_provider: provider,
        ai_model: model
      });
      
      console.log('Updated user preferences:', updateResponse);
    } catch (updateError) {
      console.warn('Error updating user preferences:', updateError);
      // Continue even if this fails
    }
    
    // Then set the model as default in the user_ai_models table
    const response = await axios.put(`${API_URL}/preferences/ai/user/models/${provider}/${model}/default`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error setting default AI model:', error);
    
    // If the specific endpoint fails, try the general preferences update
    try {
      console.log('Trying fallback to general preferences update');
      const fallbackResponse = await updateUserAIPreferences({
        ai_provider: provider,
        ai_model: model
      });
      
      if (fallbackResponse.success) {
        return {
          success: true,
          message: 'Default model set successfully (fallback)',
          fallback: true
        };
      }
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
    }
    
    return {
      success: false,
      message: error.message || 'Failed to set default AI model'
    };
  }
};
